import React, { useState, useEffect, useRef } from "react"

export default function SearchModal({ isOpen, onClose, categories, relations, onSelect }) {
  const [search, setSearch] = useState("")
  const [highlightIdx, setHighlightIdx] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 50)
  }, [isOpen])

  useEffect(() => { setHighlightIdx(0) }, [search])

  useEffect(() => {
    const filtered = categories.filter(
      (c) =>
        c.id !== 1 &&
        (c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.slug.toLowerCase().includes(search.toLowerCase()))
    )
    const max = Math.min(filtered.length - 1, 19)

    const onKey = (e) => {
      if (!isOpen) return
      if (e.key === "Escape") onClose()
      else if (e.key === "ArrowDown") { e.preventDefault(); setHighlightIdx((p) => Math.min(p + 1, max)) }
      else if (e.key === "ArrowUp") { e.preventDefault(); setHighlightIdx((p) => Math.max(p - 1, 0)) }
      else if (e.key === "Enter" && filtered[highlightIdx]) {
        e.preventDefault()
        onSelect(filtered[highlightIdx])
        onClose()
        setSearch("")
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [isOpen, onClose, search, highlightIdx, categories, onSelect])

  useEffect(() => {
    listRef.current?.children[highlightIdx]?.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }, [highlightIdx])

  if (!isOpen) return null

  const filtered = categories.filter(
    (c) =>
      c.id !== 1 &&
      (c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.slug.toLowerCase().includes(search.toLowerCase()))
  )

  const parentCount = (id) => relations.filter((r) => r.childId === id).length
  const childCount = (id) => relations.filter((r) => r.parentId === id).length

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-[500px] max-h-[70vh] bg-admin_paper rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-admin_bordered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-admin_bordered">
          <h3 className="text-admin_text font-semibold text-sm">Search Nodes</h3>
          <button
            onClick={onClose}
            className="text-admin_text_dim hover:text-admin_text transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="px-4 pt-3 pb-2">
          <input
            ref={inputRef}
            type="text"
            placeholder="Name or slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-admin_canvas border border-admin_bordered rounded-lg px-3 py-2 text-admin_text text-sm focus:outline-none focus:border-admin_primary"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5" ref={listRef}>
          {filtered.length === 0 && search === "" && (
            <p className="text-center text-admin_text_dim text-sm py-6">Start typing to search...</p>
          )}
          {filtered.length === 0 && search !== "" && (
            <p className="text-center text-admin_text_dim text-sm py-6">No nodes found</p>
          )}
          {filtered.slice(0, 20).map((cat, idx) => (
            <button
              key={cat.id}
              onClick={() => { onSelect(cat); onClose(); setSearch("") }}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                idx === highlightIdx ? "bg-admin_primary/15" : "hover:bg-admin_canvas"
              }`}
            >
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: cat.color }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-admin_text text-sm truncate">{cat.name}</div>
                <div className="text-admin_text_dim text-xs">{cat.slug}</div>
              </div>
              <div className="flex gap-2 text-xs text-admin_text_dim flex-shrink-0">
                <span title="parents">↑{parentCount(cat.id)}</span>
                <span title="children">↓{childCount(cat.id)}</span>
              </div>
            </button>
          ))}
          {filtered.length > 20 && (
            <p className="text-center text-admin_text_dim text-xs py-2">
              +{filtered.length - 20} more — refine your search
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
